import { makeAutoObservable, observable, runInAction } from "mobx";
import { track, type AnalyticsSource } from "@/lib/analytics";
import type { RootStore } from "./root.store";
import type {
  TProjectWorkflow,
  TProjectWorkflowTransition,
  TProjectWorkflowTransitionActivity,
} from "@/types/project-workflow";
import type {
  TUpdateWorkflowStateData,
  TCreateWorkflowTransitionData,
  TUpdateWorkflowTransitionData,
  TSetWorkflowMoversData,
  TSetWorkflowApproversData,
  TWorkflowApprovalData,
} from "@/services/project-workflow.service";

export class ProjectWorkflowStore {
  isLoading = false;
  isMutating = false;
  workflowsByProject: Map<string, TProjectWorkflow[]> = new Map();
  activitiesByProject: Map<string, TProjectWorkflowTransitionActivity[]> = new Map();

  private rootStore: RootStore;
  private inflightPrefetch = new Map<string, Promise<void>>();

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(
      this,
      {
        rootStore: false,
        workflowsByProject: observable,
        activitiesByProject: observable,
        inflightPrefetch: false,
      } as never,
      { autoBind: true },
    );
  }

  getWorkflows(projectId: string): TProjectWorkflow[] {
    return this.workflowsByProject.get(projectId) ?? [];
  }

  async fetchWorkflows(slug: string, projectId: string): Promise<TProjectWorkflow[]> {
    this.isLoading = true;
    try {
      const workflows = await this.rootStore.projectWorkflowService.listWorkflowStates(
        slug,
        projectId,
      );
      runInAction(() => {
        this.workflowsByProject.set(projectId, workflows);
      });
      return workflows;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  /**
   * Kick off one project's workflow-states fetch that nobody awaits — a dropdown
   * opening, an approval resolving — and keep the failure here.
   *
   * A bare `void this.fetchWorkflows(...)` in a UI handler turns any rejection
   * (a 404 for a project soft-deleted or just lost access to, a transient 5xx)
   * into an unhandled promise rejection with nothing logged; that exact shape
   * reached Sentry from the intake twin of this select as
   * NEXT-GEN-FRONTEND-1K. Losing the fetch only degrades the select to its
   * unconstrained fallback, so this warns rather than errors — but never
   * silently: the slug and project id are what make it actionable.
   */
  refreshWorkflows(slug: string, projectId: string): void {
    this.fetchWorkflows(slug, projectId).catch((err: unknown) => {
      console.warn("[project-workflow] workflow states fetch failed", { slug, projectId }, err);
    });
  }

  async prefetchWorkflows(slug: string, projectIds: string[]): Promise<void> {
    const toFetch = projectIds.filter(
      (id) => !this.workflowsByProject.has(id) && !this.inflightPrefetch.has(id),
    );
    if (toFetch.length === 0) return;
    const promise = (async () => {
      try {
        const grouped = await this.rootStore.projectWorkflowService.listWorkflowsBatch(
          slug,
          toFetch,
        );
        runInAction(() => {
          for (const id of toFetch) {
            this.workflowsByProject.set(id, grouped[id] ?? []);
          }
        });
      } catch (err) {
        // Recoverable — dropdown-open lazy fetch in `ProjectStateSelect` is the
        // fallback path for misses or transient errors.
        console.warn(
          "[project-workflow] prefetch workflows failed",
          { slug, projectIds: toFetch },
          err,
        );
      }
    })().finally(() => {
      for (const id of toFetch) this.inflightPrefetch.delete(id);
    });
    for (const id of toFetch) this.inflightPrefetch.set(id, promise);
    await promise;
  }

  async updateWorkflowState(
    slug: string,
    projectId: string,
    stateId: string,
    data: TUpdateWorkflowStateData,
    source: AnalyticsSource = "page",
  ): Promise<TProjectWorkflow> {
    this.isMutating = true;
    try {
      const updated = await this.rootStore.projectWorkflowService.updateWorkflowState(
        slug,
        projectId,
        stateId,
        data,
      );
      runInAction(() => {
        this.workflowsByProject.set(
          projectId,
          this.getWorkflows(projectId).map((w) => (w.id === updated.id ? updated : w)),
        );
      });
      track("project_workflow_state_updated", {
        project_id: projectId,
        state_id: stateId,
        fields: Object.keys(data),
        source,
      });
      return updated;
    } finally {
      runInAction(() => {
        this.isMutating = false;
      });
    }
  }

  async resetWorkflow(
    slug: string,
    projectId: string,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    this.isMutating = true;
    try {
      await this.rootStore.projectWorkflowService.resetWorkflow(slug, projectId);
      // Each write below reports before its refetch: the write landed even if
      // reloading the workflow fails.
      track("project_workflow_reset", { project_id: projectId, source });
      await this.fetchWorkflows(slug, projectId);
    } finally {
      runInAction(() => {
        this.isMutating = false;
      });
    }
  }

  async createTransition(
    slug: string,
    projectId: string,
    data: TCreateWorkflowTransitionData,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    this.isMutating = true;
    try {
      await this.rootStore.projectWorkflowService.createTransition(slug, projectId, data);
      track("project_workflow_transition_created", {
        project_id: projectId,
        workflow_id: data.workflow_id,
        to_state_id: data.transition_state_id,
        source,
      });
      await this.fetchWorkflows(slug, projectId);
    } finally {
      runInAction(() => {
        this.isMutating = false;
      });
    }
  }

  async updateTransition(
    slug: string,
    projectId: string,
    transitionId: string,
    data: TUpdateWorkflowTransitionData,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    this.isMutating = true;
    try {
      await this.rootStore.projectWorkflowService.updateTransition(
        slug,
        projectId,
        transitionId,
        data,
      );
      // Each control sends only its own setting, so the body's keys are the change.
      track("project_workflow_transition_updated", {
        project_id: projectId,
        transition_id: transitionId,
        fields: Object.keys(data),
        source,
      });
      await this.fetchWorkflows(slug, projectId);
    } finally {
      runInAction(() => {
        this.isMutating = false;
      });
    }
  }

  async deleteTransition(
    slug: string,
    projectId: string,
    transitionId: string,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    this.isMutating = true;
    try {
      await this.rootStore.projectWorkflowService.deleteTransition(slug, projectId, transitionId);
      track("project_workflow_transition_deleted", {
        project_id: projectId,
        transition_id: transitionId,
        source,
      });
      await this.fetchWorkflows(slug, projectId);
    } finally {
      runInAction(() => {
        this.isMutating = false;
      });
    }
  }

  async setMovers(
    slug: string,
    projectId: string,
    transitionId: string,
    data: TSetWorkflowMoversData,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    this.isMutating = true;
    try {
      await this.rootStore.projectWorkflowService.setMovers(slug, projectId, transitionId, data);
      track("project_workflow_transition_updated", {
        project_id: projectId,
        transition_id: transitionId,
        fields: ["movers"],
        source,
      });
      await this.fetchWorkflows(slug, projectId);
    } finally {
      runInAction(() => {
        this.isMutating = false;
      });
    }
  }

  async setApprovers(
    slug: string,
    projectId: string,
    transitionId: string,
    data: TSetWorkflowApproversData,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    this.isMutating = true;
    try {
      await this.rootStore.projectWorkflowService.setApprovers(slug, projectId, transitionId, data);
      track("project_workflow_transition_updated", {
        project_id: projectId,
        transition_id: transitionId,
        fields: ["approvers"],
        source,
      });
      await this.fetchWorkflows(slug, projectId);
    } finally {
      runInAction(() => {
        this.isMutating = false;
      });
    }
  }

  async submitApproval(
    slug: string,
    projectId: string,
    transitionId: string,
    data: TWorkflowApprovalData,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    this.isMutating = true;
    try {
      await this.rootStore.projectWorkflowService.submitApproval(
        slug,
        projectId,
        transitionId,
        data,
      );
      track("project_approval_submitted", {
        project_id: projectId,
        transition_id: transitionId,
        source,
      });
      await this.fetchWorkflows(slug, projectId);
    } finally {
      runInAction(() => {
        this.isMutating = false;
      });
    }
  }

  async submitRejection(
    slug: string,
    projectId: string,
    transitionId: string,
    data: TWorkflowApprovalData,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    this.isMutating = true;
    try {
      await this.rootStore.projectWorkflowService.submitRejection(
        slug,
        projectId,
        transitionId,
        data,
      );
      track("project_rejection_submitted", {
        project_id: projectId,
        transition_id: transitionId,
        source,
      });
      await this.fetchWorkflows(slug, projectId);
    } finally {
      runInAction(() => {
        this.isMutating = false;
      });
    }
  }

  async requestApproval(
    slug: string,
    projectId: string,
    transitionId: string,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    this.isMutating = true;
    try {
      await this.rootStore.projectWorkflowService.requestApproval(slug, projectId, transitionId);
      track("project_approval_requested", {
        project_id: projectId,
        transition_id: transitionId,
        source,
      });
      await this.fetchWorkflows(slug, projectId);
    } finally {
      runInAction(() => {
        this.isMutating = false;
      });
    }
  }

  getPendingTransitionDetails(
    projectId: string,
  ): { workflow: TProjectWorkflow; transition: TProjectWorkflowTransition } | undefined {
    const workflows = this.getWorkflows(projectId);
    for (const wf of workflows) {
      for (const tr of wf.transitions) {
        if (tr.requested_by_id) {
          return { workflow: wf, transition: tr };
        }
      }
    }
    return undefined;
  }

  getActivities(projectId: string): TProjectWorkflowTransitionActivity[] {
    return this.activitiesByProject.get(projectId) ?? [];
  }

  async fetchActivities(
    slug: string,
    projectId: string,
    params?: Record<string, string>,
  ): Promise<TProjectWorkflowTransitionActivity[]> {
    this.isLoading = true;
    try {
      const activities = await this.rootStore.projectWorkflowService.listActivity(
        slug,
        projectId,
        params,
      );
      runInAction(() => {
        this.activitiesByProject.set(projectId, activities);
      });
      return activities;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }
}
